// Subtype breakdown — per-domain analytics showing escalation rate and avg
// score grouped by (call_type, call_subtype). Helps admins see "which kinds
// of calls are dragging the team's average down" so they can adjust the
// rubric (or the rep) instead of bulk-escalating everyone.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Layers } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const escalationBadge = (rate) => {
  if (rate >= 70) return 'bg-red-100 text-red-700';
  if (rate >= 40) return 'bg-amber-100 text-amber-700';
  return 'bg-emerald-100 text-emerald-700';
};

const scoreColor = (s) => {
  if (s >= 70) return 'text-emerald-600';
  if (s >= 50) return 'text-amber-600';
  return 'text-red-600';
};

export const DomainSubtypeBreakdownTab = ({ domainId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [days, setDays] = useState(30);

  const load = (d = days) => {
    setLoading(true);
    axios
      .get(`${API}/admin/domains/${domainId}/subtype-breakdown?days=${d}`)
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => toast.error('Failed to load subtype breakdown'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(days);
     
  }, [domainId, days]);

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      const r = await axios.post(
        `${API}/admin/domains/${domainId}/recompute-subtype-status`,
      );
      toast.success(
        `Re-evaluated escalation status — ${r.data?.updated || 0} call(s) updated`,
      );
      load(days);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recompute failed');
    } finally {
      setRecomputing(false);
    }
  };

  // Group rows by call_type for cleaner display.
  const grouped = rows.reduce((acc, r) => {
    (acc[r.call_type] = acc[r.call_type] || []).push(r);
    return acc;
  }, {});
  const totals = rows.reduce(
    (a, r) => ({
      count: a.count + r.count,
      escalated: a.escalated + r.escalated,
    }),
    { count: 0, escalated: 0 },
  );

  return (
    <Card className="border-slate-200" data-testid="subtype-breakdown-tab">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-teal-500" />
            Call Sub-Intent Breakdown
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            How each call subtype is scoring. Use this to spot when a single
            subtype (e.g. cold telemarketing) is dragging the team average
            down — adjust its threshold in Knowledge Bases, then click
            "Recompute Status" to re-evaluate existing calls without a
            full LLM re-grade.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="bg-white border border-slate-200 rounded-md px-2 py-1 text-sm"
            data-testid="subtype-days-select"
          >
            <option value={7}>Last 7d</option>
            <option value={30}>Last 30d</option>
            <option value={90}>Last 90d</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRecompute}
            disabled={recomputing}
            data-testid="recompute-subtype-btn"
          >
            {recomputing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Recompute Status
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-slate-500 text-sm py-8 text-center">
            Loading subtype breakdown…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-slate-500 text-sm py-8 text-center">
            No graded calls with subtype data yet for this window. New calls
            graded from now on will be classified into subtypes automatically.
          </div>
        ) : (
          <>
            <div className="mb-4 text-xs text-slate-500">
              {totals.count} call(s), {totals.escalated} escalated
              {totals.count > 0 && (
                <> ({Math.round((totals.escalated / totals.count) * 100)}%)</>
              )}
            </div>
            <div className="space-y-5">
              {Object.entries(grouped).map(([ct, subs]) => (
                <div key={ct} data-testid={`subtype-group-${ct}`}>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">
                    {ct.replace(/_/g, ' ')}
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                          <th className="py-2 px-2">Sub-Intent</th>
                          <th className="py-2 px-2 text-right">Calls</th>
                          <th className="py-2 px-2 text-right">Escalated</th>
                          <th className="py-2 px-2 text-right">Escalation Rate</th>
                          <th className="py-2 px-2 text-right">Avg Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subs.map((r) => (
                          <tr
                            key={`${ct}-${r.subtype || 'unknown'}`}
                            className="border-b border-slate-100"
                          >
                            <td className="py-2 px-2 text-slate-700">
                              {r.subtype_label || r.subtype || (
                                <span className="italic text-slate-400">
                                  (unclassified)
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-2 text-right text-slate-700">
                              {r.count}
                            </td>
                            <td className="py-2 px-2 text-right text-slate-700">
                              {r.escalated}
                            </td>
                            <td className="py-2 px-2 text-right">
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-medium ${escalationBadge(r.escalation_rate)}`}
                              >
                                {r.escalation_rate}%
                              </span>
                            </td>
                            <td
                              className={`py-2 px-2 text-right font-semibold ${scoreColor(r.avg_score)}`}
                            >
                              {r.avg_score}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
